/**
 * Offline-Queue mit IndexedDB
 *
 * Speichert ausstehende Mutationen (GNCP-Einträge, Tagebuch, Screenings)
 * wenn der User offline ist. Synct automatisch wenn wieder online.
 *
 * Verwendung:
 *   import { enqueueOfflineMutation, processOfflineQueue, getQueueSize } from '@/lib/offlineQueue';
 *
 *   // Beim Speichern:
 *   if (!navigator.onLine) {
 *     await enqueueOfflineMutation({ table: 'food_diary', action: 'insert', data: {...} });
 *     toast.info('Offline gespeichert — wird synchronisiert sobald Sie wieder online sind.');
 *   }
 *
 *   // Beim App-Start:
 *   window.addEventListener('online', () => processOfflineQueue(supabase));
 */

const DB_NAME = 'nutriflow-offline';
const STORE_NAME = 'pending-mutations';
const DB_VERSION = 1;

export interface OfflineMutation {
  id?: number;
  table: string;
  action: 'insert' | 'update' | 'delete';
  data: Record<string, unknown>;
  filters?: Record<string, unknown>; // für update/delete
  createdAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Mutation in die Offline-Queue einreihen
 */
export async function enqueueOfflineMutation(mutation: Omit<OfflineMutation, 'id' | 'createdAt'>): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      ...mutation,
      createdAt: new Date().toISOString(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Anzahl ausstehender Mutationen
 */
export async function getQueueSize(): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Alle ausstehenden Mutationen verarbeiten (FIFO)
 */
export async function processOfflineQueue(
  supabase: { from: (table: string) => any },
): Promise<{ processed: number; failed: number }> {
  const db = await openDb();
  const mutations = await new Promise<OfflineMutation[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  if (mutations.length === 0) return { processed: 0, failed: 0 };

  let processed = 0;
  let failed = 0;

  for (const mut of mutations) {
    try {
      if (mut.action === 'insert') {
        const { error } = await supabase.from(mut.table).insert(mut.data);
        if (error) throw error;
      } else if (mut.action === 'update' && mut.filters) {
        let query = supabase.from(mut.table).update(mut.data);
        for (const [key, val] of Object.entries(mut.filters)) {
          query = query.eq(key, val);
        }
        const { error } = await query;
        if (error) throw error;
      } else if (mut.action === 'delete' && mut.filters) {
        let query = supabase.from(mut.table).delete();
        for (const [key, val] of Object.entries(mut.filters)) {
          query = query.eq(key, val);
        }
        const { error } = await query;
        if (error) throw error;
      }

      // Erfolgreich → sofort aus Queue entfernen (verhindert Duplikate bei erneutem Sync)
      await new Promise<void>((resolve, reject) => {
        const delTx = db.transaction(STORE_NAME, 'readwrite');
        delTx.objectStore(STORE_NAME).delete(mut.id!);
        delTx.oncomplete = () => resolve();
        delTx.onerror = () => reject(delTx.error);
      });
      processed++;
    } catch {
      failed++;
    }
  }

  return { processed, failed };
}

/**
 * Queue komplett leeren (z.B. nach Logout)
 */
export async function clearOfflineQueue(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
